/**
 * The visible viewport, in the client coordinate space hit-testing uses.
 * `window.innerHeight` is the layout viewport, which keeps its full height
 * under a software keyboard, while pinch-zoomed, and through mobile Safari's
 * toolbar collapse. `visualViewport` is the part actually on screen, and its
 * offsets are relative to the layout viewport — the same space
 * `getBoundingClientRect()` and `elementsFromPoint()` speak.
 */

export type ViewportRect = {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
}

const EMPTY_VIEWPORT: ViewportRect = {
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  width: 0,
  height: 0,
}

/** The on-screen viewport, in client coordinates. Zeroed during SSR. */
export function getViewportRect(): ViewportRect {
  if (typeof window === "undefined") return EMPTY_VIEWPORT

  const visual = window.visualViewport
  if (!visual) {
    // Every browser we ship to has visualViewport; this is for jsdom.
    const { innerWidth: width, innerHeight: height } = window
    return { left: 0, top: 0, right: width, bottom: height, width, height }
  }

  const { offsetLeft: left, offsetTop: top, width, height } = visual

  // Coherence guard: the visual viewport is by definition a window *into*
  // the layout viewport, so its far edges can never pass innerWidth/Height.
  // iOS has been seen resizing the layout viewport under the keyboard while
  // visualViewport kept a stale pre-resize pan; when they disagree beyond
  // rounding, trust the layout viewport the page was laid out against.
  if (
    top + height > window.innerHeight + 1 ||
    left + width > window.innerWidth + 1
  ) {
    const { innerWidth: w, innerHeight: h } = window
    return { left: 0, top: 0, right: w, bottom: h, width: w, height: h }
  }

  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
  }
}
